import React from 'react';
import { Container, Typography, Grid, Paper, Avatar, Box, IconButton, Tooltip, TextField, Button, Chip, Checkbox, FormControlLabel, Stack } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EditIcon from '@mui/icons-material/Edit';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { API_BASE } from '../config';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

export default function Team() {
  const [members, setMembers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const [form, setForm] = React.useState({ firstName: '', lastName: '', designation: '', productionDetails: '', experienceDetails: '' });
  const [submitting, setSubmitting] = React.useState(false);
  const [photoFile, setPhotoFile] = React.useState(null);
  const [photoPreview, setPhotoPreview] = React.useState(null);
  const [photoError, setPhotoError] = React.useState('');
  const [editingId, setEditingId] = React.useState(null);
  const [draggingId, setDraggingId] = React.useState(null);
  const [selectedIds, setSelectedIds] = React.useState(new Set());
  const [deleteFiles, setDeleteFiles] = React.useState(false);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/team`);
      if (res.ok) {
        const data = await res.json();
        setMembers(data);
      } else {
        console.error('Failed to fetch team', res.statusText);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { fetchMembers(); }, []);

  const handleChange = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('firstName', form.firstName || '');
      fd.append('lastName', form.lastName || '');
      fd.append('designation', form.designation || '');
      fd.append('productionDetails', form.productionDetails || '');
      fd.append('experienceDetails', form.experienceDetails || '');
      if (photoFile) fd.append('photo', photoFile);
      const url = editingId ? `${API_BASE}/api/team/${editingId}` : `${API_BASE}/api/team`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        body: fd
      });
      if (res.ok) {
        setForm({ firstName: '', lastName: '', designation: '', productionDetails: '', experienceDetails: '' });
        setPhotoFile(null);
        setPhotoPreview(null);
        setEditingId(null);
        await fetchMembers();
      } else {
        console.error('Failed to save member', await res.text());
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhotoChange = (e) => {
    setPhotoError('');
    const f = e.target.files && e.target.files[0];
    if (!f) {
      setPhotoFile(null);
      setPhotoPreview(null);
      return;
    }
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif', 'image/svg+xml'];
    const maxBytes = 5 * 1024 * 1024; // 5MB
    if (f.size > maxBytes) {
      setPhotoError('Image is too large. Maximum 5 MB allowed.');
      setPhotoFile(null);
      setPhotoPreview(null);
      return;
    }
    if (!allowed.includes(f.type)) {
      // some browsers may not provide correct MIME type for SVG; allow by extension as fallback
      const ext = f.name.split('.').pop()?.toLowerCase();
      if (!(ext === 'svg')) {
        setPhotoError('Unsupported image type. Use PNG, JPEG, WEBP, GIF or SVG.');
        setPhotoFile(null);
        setPhotoPreview(null);
        return;
      }
    }
    setPhotoFile(f);
    try {
      const url = URL.createObjectURL(f);
      setPhotoPreview(url);
    } catch (err) {
      setPhotoPreview(null);
    }
  };

  // drag & drop handlers
  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files && e.dataTransfer.files[0];
    if (f) {
      const fakeEvent = { target: { files: [f] } };
      handlePhotoChange(fakeEvent);
    }
  };
  const handleDragOver = (e) => { e.preventDefault(); };

  // simple crop: center square crop using canvas
  const cropCenterSquare = async () => {
    if (!photoFile && !photoPreview) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = photoPreview || URL.createObjectURL(photoFile);
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
    const size = Math.min(img.width, img.height);
    const sx = Math.floor((img.width - size) / 2);
    const sy = Math.floor((img.height - size) / 2);
    const canvas = document.createElement('canvas');
    const outSize = 800; // produce reasonably sized image
    canvas.width = outSize;
    canvas.height = outSize;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, sx, sy, size, size, 0, 0, outSize, outSize);
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.9));
    if (blob) {
      const croppedFile = new File([blob], (photoFile?.name || 'cropped.jpg'), { type: 'image/jpeg' });
      setPhotoFile(croppedFile);
      const url = URL.createObjectURL(croppedFile);
      setPhotoPreview(url);
      setPhotoError('');
    }
  };

  const startEdit = (member) => {
    setEditingId(member.id);
    setForm({
      firstName: member.firstName || '',
      lastName: member.lastName || '',
      designation: member.designation || '',
      productionDetails: member.productionDetails || '',
      experienceDetails: member.experienceDetails || ''
    });
            const avatarSrc = member.id ? `${API_BASE}/api/team/${member.id}/thumbnail` : null;
    setPhotoPreview(avatarSrc);
    setPhotoFile(null);
    // scroll to form
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({ firstName: '', lastName: '', designation: '', productionDetails: '', experienceDetails: '' });
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const parseTags = (text) => {
    if (!text) return [];
    return text.split(/[\n,;|]+/).map(s => s.trim()).filter(Boolean).slice(0, 4);
  };

  const saveOrder = async (orderedMembers) => {
    try {
      const ids = orderedMembers.map(m => m.id);
      await fetch(`${API_BASE}/api/team/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ids)
      });
    } catch (err) {
      console.error('Failed to save order', err);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const copy = new Set(prev);
      if (copy.has(id)) copy.delete(id); else copy.add(id);
      return copy;
    });
  };

  const handleDeleteSingle = async (id) => {
    if (!window.confirm('Delete this team member?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/team/${id}?deleteFiles=${deleteFiles}`, { method: 'DELETE' });
      if (res.ok) {
        await fetchMembers();
        // remove from selection if present
        setSelectedIds(prev => { const s = new Set(prev); s.delete(id); return s; });
      } else {
        console.error('Failed to delete member', await res.text());
      }
    } catch (err) { console.error(err); }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected member(s)?`)) return;
    try {
      const ids = Array.from(selectedIds);
      const res = await fetch(`${API_BASE}/api/team/delete?deleteFiles=${deleteFiles}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(ids)
      });
      if (res.ok) {
        setSelectedIds(new Set());
        await fetchMembers();
      } else {
        console.error('Bulk delete failed', await res.text());
      }
    } catch (err) { console.error(err); }
  };

  return (
    <Container sx={{ py: 6 }}>
      <Typography variant="h4" align="center" gutterBottom>Leadership & Team</Typography>
      <Typography align="center" color="text.secondary" sx={{ mb: 4 }}>Experienced leaders in product, engineering, data science and GenAI.</Typography>
  
  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <Stack direction="row" spacing={2} alignItems="center">
      <FormControlLabel
        control={<Checkbox checked={deleteFiles} onChange={(e) => setDeleteFiles(e.target.checked)} />}
        label="Delete files on server"
      />
      <Button
        variant="outlined"
        startIcon={<DeleteIcon />}
        color="error"
        onClick={handleBulkDelete}
        disabled={selectedIds.size === 0}
      >
        Delete selected ({selectedIds.size})
      </Button>
    </Stack>
  </Box>

  <Grid container spacing={3} sx={{ mt: 2 }}>
        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          members.map((m, idx) => {
            const tags = parseTags(m.productionDetails || m.experienceDetails);
            const initials = ((m.firstName || '').charAt(0) + (m.lastName || '').charAt(0)).toUpperCase() || '?';
            const avatarSrc = m.id ? `${API_BASE}/api/team/${m.id}/thumbnail` : null;
            return (
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
                key={m.id}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const draggedId = e.dataTransfer.getData('text/plain');
                  if (!draggedId) return;
                  const fromIndex = members.findIndex(x => String(x.id) === String(draggedId));
                  const toIndex = idx;
                  if (fromIndex === -1) return;
                  const copy = [...members];
                  const [item] = copy.splice(fromIndex, 1);
                  copy.splice(toIndex, 0, item);
                  setMembers(copy);
                  // persist order to backend
                  saveOrder(copy);
                }}
              >
                <Paper
                  sx={{
                    p: 3,
                    minHeight: 220,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    boxShadow: draggingId === m.id ? '0 10px 30px rgba(0,0,0,0.2)' : undefined,
                    transform: draggingId === m.id ? 'scale(1.02)' : undefined,
                    transition: 'box-shadow 120ms, transform 120ms'
                  }}
                  elevation={2}
                >
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Checkbox size="small" checked={selectedIds.has(m.id)} onChange={() => toggleSelect(m.id)} />
                      <Avatar src={avatarSrc} sx={{ width: 64, height: 64 }}>{!avatarSrc && initials}</Avatar>
                      <Box>
                        <Typography variant="h6">{`${m.firstName || ''} ${m.lastName || ''}`.trim()}</Typography>
                        <Typography color="text.secondary">{m.designation}</Typography>
                      </Box>
                    </Box>

                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {tags.map((t, i) => <Chip key={i} label={t} size="small" sx={{ bgcolor: 'background.paper', borderRadius: 2 }} />)}
                    </Box>

                    {(m.productionDetails || m.experienceDetails) && (
                      <Typography variant="body2" color="textSecondary">{m.experienceDetails || m.productionDetails}</Typography>
                    )}
                  </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center' }}>
                    {/* Drag handle - draggable only from the handle for clarity */}
                    <Tooltip title="Drag to reorder">
                      <IconButton
                        size="small"
                        draggable
                        onDragStart={(e) => { e.dataTransfer.setData('text/plain', m.id); e.dataTransfer.effectAllowed = 'move'; setDraggingId(m.id); }}
                        onDragEnd={() => setDraggingId(null)}
                        sx={{ cursor: 'grab' }}
                      >
                        <DragIndicatorIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => startEdit(m)}><EditIcon /></IconButton>
                    </Tooltip>
                    {m.social?.linkedin && (
                      <Tooltip title="LinkedIn">
                        <IconButton size="small" component="a" href={m.social.linkedin} target="_blank" rel="noopener noreferrer"><LinkedInIcon /></IconButton>
                      </Tooltip>
                    )}
                    {m.social?.twitter && (
                      <Tooltip title="Twitter">
                        <IconButton size="small" component="a" href={m.social.twitter} target="_blank" rel="noopener noreferrer"><TwitterIcon /></IconButton>
                      </Tooltip>
                    )}
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDeleteSingle(m.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                </Paper>
              </Grid>
            );
          })
        )}
      </Grid>

      <Box sx={{ mt: 6 }}>
        <Typography variant="h5" gutterBottom>{editingId ? 'Edit Team Member' : 'Add Team Member'}</Typography>
        <Paper sx={{ p: 3 }}>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'grid', gap: 12 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField label="First name" value={form.firstName} onChange={handleChange('firstName')} fullWidth required /></Grid>
              <Grid item xs={12} sm={6}><TextField label="Last name" value={form.lastName} onChange={handleChange('lastName')} fullWidth /></Grid>
              <Grid item xs={12}><TextField label="Designation" value={form.designation} onChange={handleChange('designation')} fullWidth /></Grid>
              <Grid item xs={12}><TextField label="Production details" value={form.productionDetails} onChange={handleChange('productionDetails')} fullWidth multiline rows={3} /></Grid>
              <Grid item xs={12}><TextField label="Experience details" value={form.experienceDetails} onChange={handleChange('experienceDetails')} fullWidth multiline rows={3} /></Grid>
              <Grid item xs={12}>
                <Box
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  sx={{ border: '2px dashed', borderColor: 'divider', p: 2, borderRadius: 1, mb: 1 }}
                >
                  <input
                    accept="image/*"
                    id="photo-upload"
                    type="file"
                    onChange={handlePhotoChange}
                    style={{ display: 'block', marginBottom: 8 }}
                  />
                  <Typography variant="body2" color="text.secondary">Drag & drop an image here, or click to select.</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Button size="small" onClick={cropCenterSquare} disabled={!photoPreview}>Crop center square</Button>
                  </Box>
                </Box>
                {photoError && <Typography color="error" sx={{ mb: 1 }}>{photoError}</Typography>}
                {photoPreview && (
                  <Box sx={{ mb: 1 }}>
                    <img src={photoPreview} alt="preview" style={{ maxHeight: 160, borderRadius: 8 }} />
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button type="submit" variant="contained" disabled={submitting || !!photoError}>{submitting ? 'Saving...' : (editingId ? 'Save changes' : 'Add member')}</Button>
                  {editingId && (
                    <Button type="button" variant="outlined" onClick={cancelEdit} disabled={submitting}>Cancel</Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}
